import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import cx from 'classnames';

import { getSubgroupsPageSortOptionAndType, setSubgroupsPageSortOptionAndType } from '../../../helpers';
import { updateSortOption, updateSortType } from '../../../store/subGroups/subGroupsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { ContainedButton, RolesButton } from '../../modules/Buttons';
import { SUBGROUP_LIST_ITEM, USER } from '../../../enums/TYPES';
import { SortOptions, SortType } from '../../../enums/SORT';
import CreateSubGroupModal from '../CreateSubGroupModal';
import DeleteSubGroupModal from '../DeleteSubGroupModal';
import { OrderSelector } from '../../modules/Selector';
import SubGroupCard from '../../modules/SubGroupCard';
import SearchInput from '../../modules/SearchInput';
import { STATUS } from '../../../enums/ENUMS';
import { ROLES } from '../../../enums/ROLES';
import styles from './SubGroupsList.module.scss';
import { useDimensions } from '../../../helpers';

type TSubGroups = {
  managementList?: USER[];
  viewersList?: USER[];
  sort: SortType;
  sortOption: SortOptions;
  width: number;
  onSubgroupSelect: (sub: SUBGROUP_LIST_ITEM) => void;
  selectedSubgroup: SUBGROUP_LIST_ITEM | null;
};

const SubGroupsList: React.FC<TSubGroups> = ({
  managementList = [],
  viewersList = [],
  sort,
  sortOption,
  width,
  onSubgroupSelect,
  selectedSubgroup,
}) => {
  const dispatch = useAppDispatch();
  const { widthD } = useDimensions();
  const isMobile = widthD <= 1024;

  const { subGroups, status } = useAppSelector((state) => state.subGroupsStore);
  const currentRole = useAppSelector((state) => state.user.role);

  const [showDeleteSubGroup, setShowDeleteSubGroup] = useState(false);
  const [subGroupsList, setList] = useState<SUBGROUP_LIST_ITEM[] | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  useEffect(() => {
    if (subGroups) {
      setList(subGroups);
    }
  }, [subGroups]);

  const switchAddModal = () => {
    setShowAddModal(!showAddModal);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setList(subGroups);
      return;
    }
    setList(subGroups!.filter((group) => group.title.toLowerCase().includes(e.target.value.toLowerCase())));
  };

  const closeDeleteModal = () => {
    setShowDeleteSubGroup(false);
  };

  const toDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteSubGroup(true);
  };

  const sortHanlder = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (option) {
      dispatch(updateSortType(option.order));
      setSubgroupsPageSortOptionAndType(option.order, option.value);
      dispatch(updateSortOption(option.value));
    } else {
      if (sortOption === singleValue) {
        const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
        dispatch(updateSortType(sortT));
        setSubgroupsPageSortOptionAndType(sortT, singleValue);
      } else {
        dispatch(updateSortType(SortType.ASC));
        setSubgroupsPageSortOptionAndType(SortType.ASC, singleValue);
      }
      dispatch(updateSortOption(singleValue));
    }
  };

  useEffect(() => {
    const res = getSubgroupsPageSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0]) {
        dispatch(updateSortType(sortAndOption[0]));
      }
      if (sortAndOption[1]) {
        dispatch(updateSortOption(sortAndOption[1]));
      }
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>Subgroups</div>
        {currentRole === ROLES.ORGANIZATION_ADMIN && (
          <ContainedButton onClick={switchAddModal}>{isMobile ? 'Create subgroup' : '+ Create a subgroup'}</ContainedButton>
        )}
      </div>

      <div className={styles.searchWrapper}>
        <SearchInput className={styles.search} onChange={onSearch} placeHolder="Search subgroup" />
        <div className={styles.toolbarRoles}>
          <OrderSelector
            title={'Sorting type'}
            selectedValue={sortOption}
            selectedOrder={sort}
            options={[
              { label: 'A-Z', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A', value: SortOptions.NAME, order: SortType.DESC },
              { label: 'Last Edit ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
              { label: 'Last Edit ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
            ]}
            onSelect={sortHanlder}
            className={styles.orderSelector}
          />
        </div>
      </div>

      <div className={styles.subgroupsList}>
        <div className={styles.subgroupListHeader}>
          <div className={styles.title} onClick={() => sortHanlder(SortOptions.NAME)}>
            Name
            {sortOption === SortOptions.NAME && sort === SortType.ASC && (
              <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
            )}
            {sortOption === SortOptions.NAME && sort === SortType.DESC && (
              <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
            )}
          </div>
          <div className={cx(styles.details, width < 680 ? styles.miniWidth : '')}>Details</div>
          {width > 680 && <div className={styles.created}>Created Date</div>}
          {width > 680 && (
            <div className={styles.updated} onClick={() => sortHanlder(SortOptions.UPDATED_AT)}>
              Edit Date{' '}
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.DESC && (
                <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
              )}
              {sortOption === SortOptions.UPDATED_AT && sort === SortType.ASC && (
                <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
              )}
            </div>
          )}
          <div className={cx(styles.action, width < 680 ? styles.miniWidth : '')}>Action</div>
        </div>
        <div className={styles.list}>
          {subGroupsList?.map((subgroup) => (
            <SubGroupCard
              key={subgroup.id}
              subgroup={subgroup}
              toDelete={toDelete}
              managementList={managementList}
              viewersList={viewersList}
              width={width}
              onSelect={onSubgroupSelect}
              selectedSubgroup={selectedSubgroup}
            />
          ))}
          {subGroups?.length === 0 && status === STATUS.IDLE && (
            <div className={styles.noSubGroups}>
              <p>No subgroups.</p>
            </div>
          )}
        </div>
      </div>
      <CreateSubGroupModal switchModal={switchAddModal} open={showAddModal} />
      <DeleteSubGroupModal id={deleteId} switchModal={closeDeleteModal} open={showDeleteSubGroup} />
    </div>
  );
};

export default SubGroupsList;
